import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';
import omit from 'lodash/omit';
import reduce from 'lodash/reduce';
import ACTION_TYPES from '../../constants/actionTypes';
import { findPniScreenId, generateTrail } from 'shared/lib/utils';

const reducers = {

  collectiblesCategories(state = [], action) {
    switch (action.type) {
      case ACTION_TYPES.GET_COLLECTIBLES_CATEGORIES_SUCCESS:
        return action.payload.categories;
      case ACTION_TYPES.SIGN_OUT:
        return [];
      default:
        return state;
    }
  },

  /*
   * Personal information
   * ========================================
   */

  collectiblesEndorsementPropertyAddress(state = {}, action) {
    switch (action.type) {
      case ACTION_TYPES.UPDATE_COLLECTIBLES_ENDORSEMENT_ADDRESS:
        return Object.assign({}, state, action.address);
      case ACTION_TYPES.SIGN_OUT:
      case ACTION_TYPES.RESET_COLLECTIBLES_ENDORSEMENT_DATA:
        return {};
      default:
        return state;
    }
  },

  collectiblesEndorsementExclusionNotice(state = null, action) {
    switch (action.type) {
      case ACTION_TYPES.SUBMIT_COLLECTIBLES_ADDRESS_SUCCESS:
        return action.data.exclusion_notice || null;
      case ACTION_TYPES.SIGN_OUT:
      case ACTION_TYPES.RESET_COLLECTIBLES_ENDORSEMENT_DATA:
        return null;
      default:
        return state;
    }
  },

  /*
   * Survey flow
   * ========================================
   */

  collectiblesEndorsementScreens(state = {}, action) {
    switch (action.type) {
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_FORM_SUCCESS:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_FORM_SUCCESS:
        return action.data.screens;
      case ACTION_TYPES.SIGN_OUT:
      case ACTION_TYPES.RESET_COLLECTIBLES_ENDORSEMENT_DATA:
        return {};
      default:
        return state;
    }
  },

  collectiblesEndorsementScreenSequence(state = [], action) {
    if (
      action.type === ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_FORM_SUCCESS
      || action.type === ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_FORM_SUCCESS
    ) {
      let dynamic_form = action.data;
      let map = [
        {
          id: dynamic_form.start_screen_id,
          parentId: null,
        },
      ];

      let trail = generateTrail(dynamic_form.screens, dynamic_form.start_screen_id, null);
      return map.concat(trail);
    } else if (action.type === ACTION_TYPES.SELECT_COLLECTIBLES_ENDORSEMENT_CHOICE) {
      let { 
        collectiblesEndorsementCurrentScreenId, 
        collectiblesEndorsementScreens 
      } = action.getState();
      let currentIndex = findIndex(state, { id: collectiblesEndorsementCurrentScreenId });
      let trailToAdd = [];
      let parentId = state[currentIndex].id;
      let trail = generateTrail(collectiblesEndorsementScreens, action.screen_id, parentId);

      let initialObj = {
        id: action.screen_id,
        parentId: parentId,
        first: true
      };
      let pniScreenId = findPniScreenId(collectiblesEndorsementScreens);
      if (action.screen_id !== pniScreenId) {
        trailToAdd.push(initialObj);
      }

      if (trail.length){
        trailToAdd = trailToAdd.concat(trail);
      }

      let start = state.slice(0, currentIndex + 1);
      let end = state.slice(currentIndex + 1);
      let newMap = start.concat(trailToAdd).concat(end);


      return newMap;
    } else if (action.type === ACTION_TYPES.COLLECTIBLES_ENDORSEMENT_SURVEY_BACK) {
      let { collectiblesEndorsementCurrentScreenId } = action.getState();
      let currentIndex = findIndex(state, { id: collectiblesEndorsementCurrentScreenId });

      if (currentIndex !== 0) {
        let current = state[currentIndex];
        let newMap = [];

        // Clear all step created from this parent
        if (current.first && current.parentId) {
          state.forEach(function(step, idx) {
            if (step.parentId !== current.parentId){
              newMap.push(step);
            }
          });
          return newMap;
        }

        return state;
      } else {
        return state;
      }
    } else {
      return state;
    }
  },

  collectiblesEndorsementCurrentScreenId(state = null, action) {
    let nextState;
    let {
      collectiblesEndorsementScreenSequence: screenSequence,
    } = (action.getState && action.getState()) || {};

    if (action.type === ACTION_TYPES.COLLECTIBLES_ENDORSEMENT_SURVEY_NEXT) {
      let currentIndex = findIndex(screenSequence, { id: state });
      let nextIndex = currentIndex + 1;

      if (screenSequence[nextIndex]) {
        nextState = screenSequence[nextIndex].id;
      } else {
        nextState = null;
      }
    } else if (action.type === ACTION_TYPES.COLLECTIBLES_ENDORSEMENT_SURVEY_BACK) {
      let currentIndex = findIndex(screenSequence, { id: state });
      let prevIndex = currentIndex - 1;

      if (currentIndex !== 0) {
        nextState = screenSequence[prevIndex].id;
      } else {
        nextState = null;
      }
    } else if (
      action.type === ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_FORM_SUCCESS
      || action.type === ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_FORM_SUCCESS
    ) {
      nextState = action.data.start_screen_id;
    } else {
      nextState = state;
    }

    return nextState;
  },

  collectiblesEndorsementCurrentResponse(state = {}, action) {
    switch (action.type) {
      case ACTION_TYPES.UPDATE_COLLECTIBLES_ENDORSEMENT_CURRENT_RESPONSE:
        return Object.assign({}, state, action.payload.response);
      case ACTION_TYPES.SAVE_COLLECTIBLES_ENDORSEMENT_RESPONSE:
      case ACTION_TYPES.COLLECTIBLES_ENDORSEMENT_SURVEY_BACK:
      case ACTION_TYPES.COLLECTIBLES_ENDORSEMENT_SURVEY_NEXT:
      case ACTION_TYPES.SIGN_OUT:
      case ACTION_TYPES.RESET_COLLECTIBLES_ENDORSEMENT_DATA:
        return {};
      default:
        return state;
    }
  },

  collectiblesEndorsementResponses(state = {}, action) {
    const newState = cloneDeep(state);
    switch (action.type) {
      case ACTION_TYPES.SAVE_COLLECTIBLES_ENDORSEMENT_RESPONSE:
        Object.assign(newState, action.response);
        return newState;
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_FORM_SUCCESS:
        return reduce(action.data.screens, (result, screen) => {
          screen.fields.forEach((field) => {
            result[field.key_name] = field.settings.default;
          });
          return result;
        }, {});
      case ACTION_TYPES.DELETE_COLLECTIBLES_ENDORSEMENT_RESPONSE:
        return omit(state, action.payload.responseKeys);
      case ACTION_TYPES.SIGN_OUT:
      case ACTION_TYPES.RESET_COLLECTIBLES_ENDORSEMENT_DATA:
        return {};
      default:
        return state;
    }
  },

  /*
   * Quote and checkout
   * ========================================
   */

  collectiblesEndorsementQuote(state = null, action) {
    switch (action.type) {
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_CHECKOUT_SUCCESS:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_CHECKOUT_SUCCESS:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_REMOVE_CHECKOUT_SUCCESS:
        return action.payload;
      case ACTION_TYPES.SAVE_COLLECTIBLES_ENDORSEMENT_RESPONSE:
      case ACTION_TYPES.SIGN_OUT:
      case ACTION_TYPES.RESET_COLLECTIBLES_ENDORSEMENT_DATA:
        return null;
      default:
        return state;
    }
  },

  collectiblesEndorsementPurchase(state = null, action) {
    switch (action.type) {
      case ACTION_TYPES.PURCHASE_COLLECTIBLES_ENDORSEMENT_ADD_SUCCESS:
      case ACTION_TYPES.PURCHASE_COLLECTIBLES_ENDORSEMENT_EDIT_SUCCESS:
      case ACTION_TYPES.SUBMIT_COLLECTIBLES_ENDORSEMENT_REMOVE_SUCCESS:
        return action.payload;
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_FORM:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_FORM:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_REMOVE_FORM:
      case ACTION_TYPES.SIGN_OUT:
        return null;
      default:
        return state;
    }
  },

  isFetchingCollectiblesEndorsementQuote(state = false, action) {
    switch (action.type) {
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_CHECKOUT:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_CHECKOUT:
        return true;
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_CHECKOUT_SUCCESS:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_CHECKOUT_FAILURE:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_CHECKOUT_SUCCESS:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_CHECKOUT_FAILURE:
        return false;
      default:
        return state;
    }
  },

  isCollectiblesEndorsementInProgress(state = false, action) {
    switch (action.type) {
      case ACTION_TYPES.GET_COLLECTIBLES_CATEGORIES:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_FORM:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_CHECKOUT:
      case ACTION_TYPES.PURCHASE_COLLECTIBLES_ENDORSEMENT_ADD:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_FORM:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_CHECKOUT:
      case ACTION_TYPES.PURCHASE_COLLECTIBLES_ENDORSEMENT_EDIT:
      case ACTION_TYPES.SUBMIT_COLLECTIBLES_ENDORSEMENT_REMOVE:
        return true;
      case ACTION_TYPES.GET_COLLECTIBLES_CATEGORIES_SUCCESS:
      case ACTION_TYPES.GET_COLLECTIBLES_CATEGORIES_FAILURE:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_FORM_SUCCESS:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_FORM_FAILURE:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_CHECKOUT_SUCCESS:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_ADD_CHECKOUT_FAILURE:
      case ACTION_TYPES.PURCHASE_COLLECTIBLES_ENDORSEMENT_ADD_SUCCESS:
      case ACTION_TYPES.PURCHASE_COLLECTIBLES_ENDORSEMENT_ADD_FAILURE:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_FORM_SUCCESS:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_FORM_FAILURE:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_CHECKOUT_SUCCESS:
      case ACTION_TYPES.GET_COLLECTIBLES_ENDORSEMENT_EDIT_CHECKOUT_FAILURE:
      case ACTION_TYPES.PURCHASE_COLLECTIBLES_ENDORSEMENT_EDIT_SUCCESS:
      case ACTION_TYPES.PURCHASE_COLLECTIBLES_ENDORSEMENT_EDIT_FAILURE:
      case ACTION_TYPES.SUBMIT_COLLECTIBLES_ENDORSEMENT_REMOVE_SUCCESS:
      case ACTION_TYPES.SUBMIT_COLLECTIBLES_ENDORSEMENT_REMOVE_FAILURE:
        return false;
      default:
        return state;
    }
  },

};

export default reducers;
